import {
  Button,
  ChannelBadge,
  type ChannelKind,
  ConfirmAction,
  DataTable,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { Pencil, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Template } from '../../schemas/template.schema';
import { formatRelativeTime } from '../../utils/date';
import { isManagedByCac, ManagedByCacBadge } from '../shared/ManagedByCac';

interface TemplateListProps {
  templates: Template[];
  loading: boolean;
  error: Error | null | undefined;
  page: number;
  pageCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onDelete: (id: string) => Promise<void>;
}

const SCOPE_TONE: Record<string, 'accent' | 'info' | 'neutral'> = {
  system: 'accent',
  tenant: 'info',
  app: 'neutral',
};

export function TemplateList({
  templates,
  loading,
  error,
  page,
  pageCount,
  pageSize,
  onPageChange,
  onDelete,
}: TemplateListProps) {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<Template>[]>(
    () => [
      {
        accessorKey: 'eventType',
        header: 'Event type',
        cell: ({ row }) => (
          <span className="font-mono text-sm text-foreground">{row.original.eventType}</span>
        ),
      },
      {
        accessorKey: 'channel',
        header: 'Channel',
        cell: ({ row }) => <ChannelBadge channel={row.original.channel as ChannelKind} />,
      },
      {
        accessorKey: 'scope',
        header: 'Scope',
        cell: ({ row }) => (
          <StatusBadge tone={SCOPE_TONE[row.original.scope] ?? 'neutral'} dot={false}>
            <span className="capitalize">{row.original.scope}</span>
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'version',
        header: 'Version',
        cell: ({ row }) => <span className="text-sm">v{row.original.version}</span>,
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.isActive ? 'success' : 'neutral'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span
            title={new Date(row.original.updatedAt).toLocaleString()}
            className="text-xs text-muted-foreground"
          >
            {formatRelativeTime(row.original.updatedAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        // Una plantilla del CaC no se edita ni se borra acá: candado en vez de botones.
        cell: ({ row }) =>
          isManagedByCac(row.original) ? (
            <div className="flex justify-end">
              <ManagedByCacBadge />
            </div>
          ) : (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Edit template"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/templates/${row.original.id}/edit`);
                }}
              >
                <Pencil />
              </Button>
              <ConfirmAction
                trigger={
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete template"
                    className="text-destructive hover:text-destructive"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Trash2 />
                  </Button>
                }
                title="Delete this template?"
                description={`This will deactivate '${row.original.eventType} · ${row.original.channel}'. It can be restored later.`}
                confirmLabel="Delete"
                destructive
                onConfirm={() => onDelete(row.original.id)}
              />
            </div>
          ),
      },
    ],
    [navigate, onDelete],
  );

  return (
    <DataTable
      columns={columns}
      data={templates}
      isLoading={loading}
      error={error}
      pageCount={pageCount}
      pagination={{ pageIndex: page - 1, pageSize }}
      onPaginationChange={(state) => onPageChange(state.pageIndex + 1)}
      onRowClick={(t) => navigate(`/templates/${t.id}/edit`)}
    />
  );
}
